import React, { useEffect, useRef, useState } from 'react';
import { hinnastoData } from './hinnastoData';
import './hinnastoNew.css';

/**
 * Apufunktio sähköpostien ja linkkien automaattiseen tunnistamiseen tekstistä
 */
const renderTextWithLinks = (text) => {
  if (typeof text !== 'string') return text;

  const linkRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matchedText = match[0];

    if (matchedText.includes('@')) {
      parts.push(
        <a key={match.index} href={`mailto:${matchedText}`}>
          {matchedText}
        </a>
      );
    } else {
      const href = matchedText.startsWith('www.') ? `https://${matchedText}` : matchedText;
      parts.push(
        <a key={match.index} href={href} target="_blank" rel="noopener noreferrer">
          {matchedText}
        </a>
      );
    }

    lastIndex = linkRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const HinnastoNew = () => {
  const [activeSection, setActiveSection] = useState(hinnastoData.tpo.id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0 
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace('#', '');
      if (id && sectionRefs.current[id]) {
        sectionRefs.current[id].scrollIntoView({ behavior: 'smooth' });
      }
    };

    const timeoutId = setTimeout(scrollToHash, 100);

    window.addEventListener('hashchange', scrollToHash);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  const scrollToSection = (id) => {
    if (sectionRefs.current[id]) {
      sectionRefs.current[id].scrollIntoView({ behavior: 'smooth' });
    }
  };

  const categories = Object.keys(hinnastoData).filter(key => hinnastoData[key] && hinnastoData[key].id);

  return (
    <div className="hinnasto-stripe-layout">

      <section className="hero-header">
        <h1>{hinnastoData.header.title}</h1>
        <p>{hinnastoData.header.subtitle}</p>

        <div className="hero-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path
              d="M0,80 C300,10 900,140 1200,50 L1200,120 L0,120 Z"
              fill="#b515d1"
              opacity="0.10"
            />
            <path
              d="M0,64 C300,120 900,0 1200,64 L1200,120 L0,120 Z"
              fill="#e8eaec"
            />
          </svg>
        </div>
      </section>

      <div className="stripe-main-container">
        <aside className="stripe-sidebar">
          <h3 className="sidebar-label">Sisältö</h3>
          <ul className="sidebar-links">
            {categories.map(key => (
              <li key={hinnastoData[key].id}>
                <button 
                  onClick={() => scrollToSection(hinnastoData[key].id)}
                  className={activeSection === hinnastoData[key].id ? 'active' : ''}
                >
                  {hinnastoData[key].label}
                </button>
              </li>
            ))}
          </ul>
          
          <div className="billing-callout">
            <h3>Laskutuskäytännöt</h3>
            <p>Lue lisää lukukausimaksujen laskutuksesta, eräpäivistä ja maksuaikamahdollisuuksista.</p>
            <a href="/maksuohjeet" className="billing-callout-link">Katso laskutusohjeet →</a>
          </div>
          
          {hinnastoData.yleisetHuomautukset && (
            <div className="general-notice-container general-notice-container--desktop">
              {hinnastoData.yleisetHuomautukset.map((item, i) => (
                <div key={i} className="general-notice">
                  <svg className="general-notice-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="9" stroke="#635bff" strokeWidth="1.5"/>
                    <path d="M10 9v5" stroke="#635bff" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="10" cy="6.5" r="1" fill="#635bff"/>
                  </svg>
                  <p>
                    {typeof item === "string" ? renderTextWithLinks(item) : (
                      <>
                        {item.teksti}&nbsp;
                        <a href={item.linkki.href}>{item.linkki.teksti}</a>
                        {item.jatko && <>&nbsp;{item.jatko}</>}
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
        </aside>
       
        <main className="stripe-content">
          {categories.map(key => {
            const section = hinnastoData[key];
            return (
              <section 
                key={section.id} 
                id={section.id} 
                ref={(el) => (sectionRefs.current[section.id] = el)}
                className="content-section"
              >
                <h2 style={{ marginBottom: "9px"}}>{section.label}</h2>
                
                {/* Kuvaus */}
                {Array.isArray(section.kuvaus) ? (
                  <div className="section-description">
                    {section.kuvaus.map((paragraph, i) => (
                      <p key={i}>{renderTextWithLinks(paragraph)}</p>
                    ))}
                    {section.alennus && (
                      <p style={{ fontSize: "14px", fontWeight: "bold", marginTop: "6px"}}>
                        ({renderTextWithLinks(section.alennus)})
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="section-description">{renderTextWithLinks(section.kuvaus)}</p>
                )}
                
                {/* Hinnastotaulukko */}
                <table className="price-table">
                  <tbody>
                    {section.hinnat.map((item, index) => (
                      <tr key={index}>
                        <td style={{paddingLeft: "10px"}} className="price-name">{item.nimi}</td>
                        <td style={{paddingRight: "10px"}} className="item-price"><strong>{item.hinta}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* HINNASTON ALAPUOLELLE TULEVAT LISÄTIEDOT (MUSKARI & AVOIN) */}
                {section.lisatiedot && (
                  <div className="section-extra-info" style={{ marginTop: '15px', marginBottom: '15px', fontSize: '14px', lineHeight: '1.6' }}>
                    {section.lisatiedot.map((info, idx) => (
                      <p key={idx} style={{ marginBottom: '6px', color: '#333' }}>
                        • {renderTextWithLinks(info)}
                      </p>
                    ))}
                  </div>
                )}
               
                {/* OPINTOJEN PERUMINEN (AVATTAVA LAATIKKO / DETAILS) */}
                {section.peruutusehdot && (
                  <div className="exceptions-container" style={{ marginTop: '15px' }}>
                    <details className="exception-item">
                      <summary>Opintojen peruminen</summary>
                      <div className="exception-content">
                        {Object.entries(section.peruutusehdot).map(([termKey, value]) => (
                          <div key={termKey} style={{ marginBottom: '12px' }}> 
                            {termKey !== "yleiset" && (
                              <h5 style={{ fontSize: '15px', textTransform: 'capitalize', marginBottom: '6px' }}>
                                {termKey === 'uudet' ? 'Uudet oppilaat' : termKey === 'jatkavat' ? 'Jatkavat oppilaat' : termKey}
                              </h5>
                            )}
                            
                            {Array.isArray(value) ? (
                              <ul className="terms-list">
                                {value.map((line, i) => (
                                  <li key={i}>{renderTextWithLinks(line)}</li>
                                ))}
                              </ul>
                            ) : (
                              <p style={{ fontSize: '14px' }}>
                                {renderTextWithLinks(value)}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}

                {/* POIKKEUSTILANTEET (Maksuhuojennukset & Opintojen keskeyttäminen) VAIN TPO:LLE */}
                {key === "tpo" && hinnastoData.poikkeustilanteet && (
                  <div className="exceptions-container" style={{ marginTop: '10px' }}>
                    {hinnastoData.poikkeustilanteet.map((item, i) => (
                      <details key={i} className="exception-item">
                        <summary>{item.otsikko}</summary>
                        <div className="exception-content">
                          {item.sisalto.map((paragraph, j) => (
                            <p key={j}>{renderTextWithLinks(paragraph)}</p>
                          ))}
                          {item.alaosio && (
                            <div className="exception-subsection">
                              <h5>{item.alaosio.otsikko}</h5>
                              <p>{renderTextWithLinks(item.alaosio.teksti)}</p>
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </main>
      </div>

      {/* Yleiset huomautukset mobiilissa */}
      {hinnastoData.yleisetHuomautukset && (
        <div className="general-notice-container general-notice-container--mobile">
          {hinnastoData.yleisetHuomautukset.map((item, i) => (
            <div key={i} className="general-notice">
              <svg className="general-notice-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9" stroke="#635bff" strokeWidth="1.5"/>
                <path d="M10 9v5" stroke="#635bff" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="10" cy="6.5" r="1" fill="#635bff"/>
              </svg>
              <p>
                {typeof item === "string" ? renderTextWithLinks(item) : (
                  <>
                    {item.teksti}&nbsp;
                    <a href={item.linkki.href}>{item.linkki.teksti}</a>
                    {item.jatko && <>&nbsp;{item.jatko}</>}
                  </>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Alapalkki mobiilissa */}
      <div className="billing-callout billing-callout--mobile">
        <span>Kysymyksiä laskutuksesta?</span>
        <a href="/maksuohjeet" className="billing-callout-link">Katso ohjeet →</a>
      </div>
    </div>
  );
};

export default HinnastoNew;